import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';
import { getProductById } from '../data/products';
import { Product } from '../types';
import AffiliateCard from '../components/AffiliateCard';

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    if (id) {
      const productData = getProductById(id);
      setProduct(productData || null);
    }
    setLoading(false);
  }, [id]);

  if (loading) {
    return (
      <div className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center py-16">
            <h1 className="text-3xl font-bold text-gray-800 mb-4">Produkt nicht gefunden</h1>
            <p className="text-gray-600 mb-8">
              Das angeforderte Produkt existiert nicht oder wurde entfernt.
            </p>
            <Link 
              to="/"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <ArrowLeft className="mr-2 w-5 h-5" />
              Zurück zur Startseite
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // For demo purposes we're using the same image, but in a real app you'd have multiple images
  const productImages = [
    product.image,
    'https://images.pexels.com/photos/404280/pexels-photo-404280.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    'https://images.pexels.com/photos/341523/pexels-photo-341523.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
  ];

  // Helper function to render price
  const renderPrice = () => {
    if (product.monatlich && typeof product.monatlich === 'number') {
      return (
        <div className="flex items-baseline mb-6">
          <span className="text-3xl font-bold text-blue-600">{product.monatlich.toFixed(2)} €</span>
          <span className="ml-2 text-sm text-gray-600">/Monat</span>
        </div>
      );
    }
    
    if (typeof product.price === 'number') {
      return (
        <div className="flex items-baseline mb-6">
          <span className="text-3xl font-bold text-blue-600">{product.price.toFixed(2)} €</span>
          {product.oldPrice && typeof product.oldPrice === 'number' && product.oldPrice > 0 && (
            <>
              <span className="ml-2 text-xl text-gray-500 line-through">{product.oldPrice.toFixed(2)} €</span>
              <span className="ml-2 text-sm text-green-600 font-medium">
                {Math.round((1 - product.price / product.oldPrice) * 100)}% Rabatt
              </span>
            </>
          )}
        </div>
      );
    }
    
    return (
      <div className="flex items-baseline mb-6">
        <span className="text-2xl font-bold text-blue-600">Preis auf Anfrage</span>
      </div>
    );
  };

  const productPrice = product.monatlich
    ? `${product.monatlich.toFixed(2)} €/Monat`
    : typeof product.price === 'number'
    ? `${product.price.toFixed(2)} €`
    : 'Preis auf Anfrage';

  return (
    <div className="pt-24 pb-16">
      <Helmet>
        <title>{product.name} | Easy Vertrag - Beste Preise</title>
        <meta name="description" content={`${product.name} - ${product.description?.slice(0, 120)}. Jetzt für ${productPrice} bei Easy Vertrag kaufen.`} />
        <meta name="keywords" content={`${product.name}, ${product.category}, Handyvertrag, Easy Vertrag, kaufen`} />
        <link rel="canonical" href={`https://easy4vertrag.de/produkt/${id}`} />
        <meta property="og:title" content={`${product.name} - Easy Vertrag`} />
        <meta property="og:description" content={`${product.name} für ${productPrice}. ${product.description?.slice(0, 100)}`} />
        <meta property="og:url" content={`https://easy4vertrag.de/produkt/${id}`} />
        <meta property="og:image" content={product.image} />
        <meta property="og:type" content="product" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {"@type": "ListItem", "position": 1, "name": "Startseite", "item": "https://easy4vertrag.de/"},
              {"@type": "ListItem", "position": 2, "name": "${product.category}", "item": "https://easy4vertrag.de/produkte/${product.category}"},
              {"@type": "ListItem", "position": 3, "name": "${product.name}", "item": "https://easy4vertrag.de/produkt/${id}"}
            ]
          }
        `}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "${product.name}",
            "image": "${product.image}",
            "description": "${product.description?.replace(/"/g, '\\"')}",
            "brand": {
              "@type": "Brand",
              "name": "Easy Vertrag"
            },
            "offers": {
              "@type": "Offer",
              "url": "https://easy4vertrag.de/produkt/${id}",
              "priceCurrency": "EUR",
              "availability": "https://schema.org/InStock",
              "seller": {
                "@type": "Organization",
                "name": "Easy Vertrag"
              }
            }
          }
        `}</script>
      </Helmet>
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <Link
            to={`/produkte/${product.category}`}
            className="inline-flex items-center text-blue-600 hover:text-blue-800"
          >
            <ArrowLeft className="mr-1 w-4 h-4" />
            <span>Zurück zur Kategorie</span>
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-10">
          <div className="grid md:grid-cols-2 gap-8 p-6 lg:p-8">
            {/* Product Images */}
            <div>
              <div className="rounded-lg overflow-hidden bg-gray-100 mb-4">
                <img 
                  src={productImages[activeImageIndex]} 
                  alt={product.name} 
                  className="w-full h-full object-contain aspect-square"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                {productImages.map((img, index) => (
                  <button
                    key={index}
                    className={`border rounded-lg overflow-hidden p-1 ${
                      activeImageIndex === index ? 'border-blue-600' : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setActiveImageIndex(index)}
                  >
                    <img 
                      src={img} 
                      alt={`${product.name} - Bild ${index + 1}`} 
                      className="w-full h-16 object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Product Info */}
            <div>
              {product.isNew && (
                <span className="inline-block bg-blue-600 text-white text-xs px-2 py-1 rounded mb-4">
                  NEU
                </span>
              )}
              <h1 className="text-3xl font-bold text-gray-800 mb-4">{product.name}</h1>
              
              {renderPrice()}
              
              <p className="text-gray-600 mb-6">{product.description}</p>
              
              <h3 className="text-lg font-bold text-gray-800 mb-3">Produktmerkmale:</h3>
              <ul className="mb-8">
                {product.features.map((feature, index) => (
                  <li key={index} className="flex items-start mb-2">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mr-2 mt-0.5" />
                    <span className="text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <h3 className="text-lg font-bold text-gray-800 mb-3"></h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Array.isArray(product.affiliateLinks) && product.affiliateLinks.map((affiliate, index) => (
                  <AffiliateCard key={index} affiliate={affiliate} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Related Products Section (Placeholder) */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Ähnliche Produkte</h2>
          <div className="bg-gray-100 p-12 rounded-lg text-center">
            <p className="text-gray-600">
              Hier würden ähnliche Produkte angezeigt werden.
            </p>
          </div>
        </section>

        {/* Technical Specifications */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Technische Daten</h2>
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <table className="w-full">
              <tbody>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 bg-gray-50">Modell</th>
                  <td className="py-3 px-4">{product.name}</td>
                </tr>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 bg-gray-50">Kategorie</th>
                  <td className="py-3 px-4">{
                    product.category === 'smartphones' ? 'Smartphone' :
                    product.category === 'tablets' ? 'Tablet' :
                    product.category === 'smartwatches' ? 'Smartwatch' :
                    product.category === 'kopfhorer' ? 'Kopfhörer' :
                    product.category === 'handytarif' ? 'Handytarif' : 
                    product.category
                  }</td>
                </tr>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 bg-gray-50">Hersteller</th>
                  <td className="py-3 px-4">Apple</td>
                </tr>
                <tr>
                  <th className="text-left py-3 px-4 bg-gray-50">Lieferzeit</th>
                  <td className="py-3 px-4">2-3 Werktage</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ProductDetailPage;